import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './HomePage.css';
import logo from '../assets/logos/dama.jpeg';
import dama1 from '../assets/images/dama1.png';
import dama5 from '../assets/images/dama5.png';
import DataWheel from '../components/DataWheel';
import missionImg from '../assets/images/Mission.png';
import visionImg from '../assets/images/Vision.png';
import carteImg from '../assets/images/carte.png';
import interImg from '../assets/images/inter.png';

const HomePage = () => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const navLinksRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);
  const [committeeVisible, setCommitteeVisible] = useState(false);
  const committeeRef = useRef(null);

  /* ── Auth state ── */
  const [currentUser, setCurrentUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  /* ── Lire session ── */
  useEffect(() => {
    const user = localStorage.getItem('dama_current_user');
    if (user) setCurrentUser(JSON.parse(user));
    else setCurrentUser(null);
  }, []);

  /* ── Fermer dropdown si click dehors ── */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('dama_current_user');
    setCurrentUser(null);
    setDropdownOpen(false);
  };

  const getInitials = (user) => {
    if (user.prenom && user.nom) return `${user.prenom[0]}${user.nom[0]}`.toUpperCase();
    return user.email[0].toUpperCase();
  };

  const getFirstName = (user) => user.prenom || user.email.split('@')[0];

  /* ── Auto-scroll vers lien actif sur mobile ── */
  useEffect(() => {
    if (navLinksRef.current) {
      const activeLink = navLinksRef.current.querySelector('a.active');
      if (activeLink) {
        activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [location.pathname]);

  /* ── Committee animation ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCommitteeVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (committeeRef.current) observer.observe(committeeRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCardClick = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  return (
    <div className="homepage">

      {/* ===== NAVBAR ===== */}
      <nav className="navbar">

        {/* ── ROW 1 : Logo + Actions ── */}
        <div className="navbar-inner">

          <Link to="/" className="navbar-logo" style={{ textDecoration: 'none' }}>
            <img src={logo} alt="DAMA Tunisia Logo" className="logo-img" />
            <div className="logo-text-block">
              <div className="logo-title-row">
                <span className="logo-text">DAMA <span className="logo-tn">Tunisia</span></span>
                <span className="logo-forming">(Forming Chapter)</span>
              </div>
              <span className="logo-subtitle">Together, towards responsible DATA</span>
            </div>
          </Link>

          <div className="navbar-actions">
            {currentUser ? (
              <div className="hp-user-pill-wrapper" ref={dropdownRef}>
                <button
                  className="hp-user-pill"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <div className="hp-user-avatar">{getInitials(currentUser)}</div>
                  <span className="hp-user-name">{getFirstName(currentUser)}</span>
                  <svg
                    className={`hp-chevron ${dropdownOpen ? 'open' : ''}`}
                    width="14" height="14" viewBox="0 0 14 14"
                    fill="none" xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="hp-user-dropdown">
                    <div className="hp-dropdown-header">
                      <div className="hp-dropdown-avatar">{getInitials(currentUser)}</div>
                      <div>
                        <p className="hp-dropdown-name">{currentUser.prenom} {currentUser.nom}</p>
                        <p className="hp-dropdown-email">{currentUser.email}</p>
                      </div>
                    </div>
                    <div className="hp-dropdown-divider" />
                    <button className="hp-dropdown-logout" onClick={handleLogout}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" state={{ mode: 'login' }} className="btn-login">LOGIN</Link>
                <Link to="/login" state={{ mode: 'subscribe' }} className="btn-nav">Subscribe to our Newsletter</Link>
              </>
            )}

            <a href="https://www.linkedin.com/company/dama-tunisia/posts/?feedView=all" target="_blank" rel="noreferrer" className="btn-linkedin">in</a>
            <a href="https://www.facebook.com/people/DAMA-Tunisia-Chapter/61575921963330/" target="_blank" rel="noreferrer" className="btn-facebook">f</a>
          </div>

        </div>

        {/* ── ROW 2 : Nav links full-width + scroll mobile ── */}
        <div className="navbar-links-row">
          <div className="navbar-bottom" ref={navLinksRef}>
            <Link to="/news"           className={isActive('/news')           ? 'active' : ''}>News</Link>
            <Link to="/events"         className={isActive('/events')         ? 'active' : ''}>Events</Link>
            <Link to="/learning"       className={isActive('/learning')       ? 'active' : ''}>Learning</Link>
            <Link to="/insights"       className={isActive('/insights')       ? 'active' : ''}>Insights</Link>
            <Link to="/chapters-board" className={isActive('/chapters-board') ? 'active' : ''}>Chapters Board</Link>
            <Link to="/about"          className={isActive('/about')          ? 'active' : ''}>About Us</Link>
            <Link to="/membership"     className={isActive('/membership')     ? 'active' : ''}>Become a Member</Link>
          </div>
        </div>

      </nav>

      {/* ===== HERO BANNER ===== */}
      <section className="hero-banner">
        <div className="hero-banner-content">
          <h1>Who is DAMA Tunisia?</h1>
        </div>
      </section>

      {/* ===== WHO WE ARE / WHY JOIN ===== */}
      <section className="info-toggle-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title-about">About DAMA Tunisia</h2>
          </div>

          <div className="info-cards-row">
            <div
              className={`info-card ${activeSection === 'who' ? 'info-card-active' : ''}`}
              onClick={() => handleCardClick('who')}
            >
              <div className="info-card-icon">🌐</div>
              <h2>Who We Are</h2>
              <span className="info-card-arrow">{activeSection === 'who' ? '▲' : '▼'}</span>
            </div>

            <div
              className={`info-card info-card-orange ${activeSection === 'why' ? 'info-card-active' : ''}`}
              onClick={() => handleCardClick('why')}
            >
              <div className="info-card-icon">🤝</div>
              <h2>Why Join DAMA Tunisia?</h2>
              <span className="info-card-arrow">{activeSection === 'why' ? '▲' : '▼'}</span>
            </div>
          </div>

          {activeSection === 'who' && (
            <div className="expanded-content">
              <div className="content-banner" style={{ backgroundImage: `url(${dama1})` }}>
                <div className="content-banner-overlay" />
                <h2 className="content-banner-title">Who We Are</h2>
              </div>
              <div className="mv-cards">
                <div className="mv-card">
                  <div className="mv-icon-col">
                    <div className="mv-bar mv-bar-green" />
                    <div className="mv-icon">
                      <img src={missionImg} alt="Mission" className="mv-img" />
                    </div>
                  </div>
                  <div className="mv-text">
                    <h3>Mission :</h3>
                    <p>
                      DAMA Tunisia serves Tunisian data professionals by providing a community
                      space for knowledge sharing, recognizing professional achievement, and
                      promoting ethical and effective data practices, resulting in responsible
                      and effective data management across Tunisia.
                    </p>
                  </div>
                </div>
                <div className="mv-card">
                  <div className="mv-icon-col">
                    <div className="mv-bar mv-bar-blue" />
                    <div className="mv-icon">
                      <img src={visionImg} alt="Vision" className="mv-img" />
                    </div>
                  </div>
                  <div className="mv-text">
                    <h3>Vision :</h3>
                    <p>
                      Enabling leading data and information management practices in Tunisia,
                      resulting in positive national and global outcomes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'why' && (
            <div className="expanded-content">
              <div className="content-banner" style={{ backgroundImage: `url(${dama5})` }}>
                <div className="content-banner-overlay" />
                <h2 className="content-banner-title">Why Join DAMA Tunisia?</h2>
              </div>
              <div className="why-grid">
                <div className="why-item" style={{'--card-color': '#7C3AED', '--card-shadow': 'rgba(124,58,237,0.22)'}}>
                  <div className="why-icon-wrap">👥</div>
                  <div className="why-text"><h4>Community</h4><p>We unite a diverse network of data practitioners, providing platforms for networking, collaboration, and the advancement of the data management profession.</p></div>
                </div>
                <div className="why-item" style={{'--card-color': '#0EA5E9', '--card-shadow': 'rgba(14,165,233,0.22)'}}>
                  <div className="why-icon-wrap">🎓</div>
                  <div className="why-text"><h4>Capability</h4><p>As the standard-bearer for data management education, we offer resources like the DAMA-DMBOK® 2 Revised Edition and the CDMP® certification, enabling continuous skill development.</p></div>
                </div>
                <div className="why-item" style={{'--card-color': '#92400E', '--card-shadow': 'rgba(146,64,14,0.18)'}}>
                  <div className="why-icon-wrap">💼</div>
                  <div className="why-text"><h4>Career</h4><p>We facilitate your professional growth by offering clear pathways for career advancement, including access to exclusive resources, discounts on educational materials, and opportunities to connect with industry leaders.</p></div>
                </div>
                <div className="why-item" style={{'--card-color': '#D97706', '--card-shadow': 'rgba(217,119,6,0.22)'}}>
                  <div className="why-icon-wrap">⭐</div>
                  <div className="why-text"><h4>Leadership</h4><p>DAMA Tunisia sets the standard for data management excellence, guiding professionals, organizations, and industries toward ethical and effective data practices through advocacy, research, and thought leadership.</p></div>
                </div>
                <div className="why-item" style={{'--card-color': '#DB2777', '--card-shadow': 'rgba(219,39,119,0.20)'}}>
                  <div className="why-icon-wrap">🧠</div>
                  <div className="why-text"><h4>Knowledge</h4><p>Stay ahead in the ever-evolving data landscape with access to cutting-edge research, industry best practices, and expert insights. From the DAMA-DMBOK® to exclusive member resources, we provide the knowledge you need to excel.</p></div>
                </div>
                <div className="why-item" style={{'--card-color': '#F59E0B', '--card-shadow': 'rgba(245,158,11,0.22)'}}>
                  <div className="why-icon-wrap">💡</div>
                  <div className="why-text"><h4>Innovation</h4><p>We empower data professionals to harness emerging technologies and innovative methodologies to solve complex data challenges, fostering a forward-thinking mindset for data-driven decision-making.</p></div>
                </div>
                <div className="why-item why-item-full" style={{'--card-color': '#D97706', '--card-shadow': 'rgba(217,119,6,0.20)'}}>
                  <div className="why-icon-wrap">⚖️</div>
                  <div className="why-text"><h4>Ethics &amp; Governance</h4><p>Data is a powerful asset, and with great power comes great responsibility. DAMA Tunisia champions ethical data management practices, advocating for strong governance frameworks that ensure transparency, compliance, and trust in data usage.</p></div>
                </div>
              </div>
              <div className="why-buttons">
                <Link to="/membership" className="why-btn">Join DAMA Tunisia</Link>
                <Link to="/about" className="why-btn">FAQ</Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===== DAMA FRAMEWORK ===== */}
      <section className="mission-section">
        <div className="mission-container">
          <div className="mission-left">
            <h2 className="mission-title">The DAMA Data Management Framework</h2>
            <p className="mission-text">
              The <strong>DAMA-DMBOK®</strong> defines <strong>11 essential knowledge areas</strong> for
              effective data management. As the Tunisian chapter of DAMA International,
              <strong> DAMA Tunisia</strong> helps professionals master these disciplines to drive
              data-driven decision making across organizations.
            </p>
            <h2 className="mission-title">Why It Matters</h2>
            <ul className="mission-objectives">
              <li><strong>Data Governance</strong> sits at the core, overseeing all 10 surrounding knowledge areas to ensure consistency, quality, and accountability.</li>
              <li>From <strong>Data Architecture</strong> to <strong>Data Quality</strong>, each discipline plays a strategic role in managing data as a key organizational asset.</li>
              <li>DAMA Tunisia connects professionals with the tools, certifications, and community to master these frameworks across Tunisia.</li>
            </ul>
          </div>
          <div className="mission-right">
            <DataWheel />
          </div>
        </div>
      </section>

      {/* ===== CARTE SECTION ===== */}
      <section className="carte-section">
        <div className="carte-container">
          <img src={carteImg} alt="Carte DAMA Tunisia" className="carte-img" />
        </div>
      </section>

      {/* ===== OUR MEMBERS ===== */}
      <section className="committee-section">
        <div className="committee-container">
          <h2 className="committee-title">Our Members</h2>
          <div ref={committeeRef} className={`committee-grid ${committeeVisible ? 'committee-visible' : ''}`}>
            {[...Array(5)].map((_, i) => (
              <div className="committee-card" key={i}>
                <div className="committee-avatar"><span>👤</span></div>
                <a href="https://www.linkedin.com/company/dama-tunisia" target="_blank" rel="noreferrer" className="committee-linkedin">
                  <span className="li-icon">in</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PARTNERS & SPONSORS ===== */}
      <section className="partners-section">
        <div className="partners-container">
          <h2 className="partners-title">Our Partners and Sponsors</h2>
          <div className="partners-grid">
            <div className="partner-card">
              <img src={interImg} alt="DAMA International" className="partner-img" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-brand">
            <img src={logo} alt="DAMA Tunisia" className="footer-logo" />
            <span>DAMA Tunisia</span>
            <p>The Tunisian chapter of DAMA International.<br />Promoting data management in Tunisia.</p>
          </div>
          <div className="footer-links">
            <h4>Navigation</h4>
            <Link to="/about">About Us</Link>
            <Link to="/learning">Learning</Link>
            <Link to="/events">Events</Link>
            <Link to="/membership">Membership</Link>
          </div>
          <div className="footer-links">
            <h4>Resources</h4>
            <Link to="/learning">CDMP®</Link>
            <Link to="/dmbok">DMBoK®</Link>
            <Link to="/events">Webinars</Link>
            <Link to="/news">Newsletter</Link>
          </div>
          <div className="footer-contact">
            <h4>Contact</h4>
            <p>📧 contact@dama-tn.org</p>
            <p>📍 Tunis, Tunisia</p>
            <div className="social-links">
              <a href="https://www.linkedin.com/company/dama-tunisia/posts/?feedView=all" target="_blank" rel="noreferrer">LinkedIn</a>
              <a href="https://www.facebook.com/people/DAMA-Tunisia-Chapter/61575921963330/" target="_blank" rel="noreferrer">Facebook</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 DAMA Tunisia. All rights reserved. | Affiliated chapter of DAMA International</p>
        </div>
      </footer>

    </div>
  );
};

export default HomePage;